import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { DeliveryPane } from "../components/comms/DeliveryPane";
import { DeliveryBadge, SendSampleButton, KillSwitchBar, SenderHealthBar } from "../components/comms/DeliveryControls";
import { toast } from "sonner";
import {
  Inbox, LayoutTemplate, Zap, Users, ShieldOff, BarChart3, ShieldAlert,
  Loader2, Mail, Search, ToggleLeft, ToggleRight, Eye, Plus, X, Trash2, Radio
} from "lucide-react";

const TABS = [
  { key: "history",       label: "Email History",       Icon: Inbox },
  { key: "verifications", label: "Verifications",       Icon: ShieldAlert },
  { key: "delivery",      label: "Delivery (Live/Test)", Icon: Radio },
  { key: "templates",     label: "Templates",           Icon: LayoutTemplate },
  { key: "automations",   label: "Automations",         Icon: Zap },
  { key: "lists",         label: "Distribution Lists",  Icon: Users },
  { key: "suppressions",  label: "Suppressions",        Icon: ShieldOff },
];

export default function CommunicationsCenter() {
  const [tab, setTab] = useState("history");
  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="communications-center">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-[#0A2540]">Communications Center</h1>
        <p className="text-sm text-slate-500 mt-1">Templates, automations, and every email that leaves the platform.</p>
      </div>

      <div className="flex items-center gap-1 border-b border-slate-200 mb-6 overflow-x-auto">
        {TABS.map(t => {
          const active = tab === t.key;
          const Icon = t.Icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition whitespace-nowrap ${
                active ? "border-[#0066FF] text-[#0066FF]" : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
              data-testid={`comms-tab-${t.key}`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      <SenderHealthBar />
      <KillSwitchBar />
      {tab === "history" && <HistoryPane />}
      {tab === "verifications" && <VerificationsPane />}
      {tab === "delivery" && <DeliveryPane />}
      {tab === "templates" && <TemplatesPane />}
      {tab === "automations" && <AutomationsPane />}
      {tab === "lists" && <DistributionListsPane />}
      {tab === "suppressions" && <SuppressionsPane />}
    </div>
  );
}

// -------------------- History --------------------
function HistoryPane() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get("/comms/deliveries", { params: { limit: 500 } })
      .then(r => setRows(r.data || []))
      .finally(() => setLoading(false));
  }, []);

  const filtered = rows.filter(r => {
    if (statusFilter && r.status !== statusFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      if (!(r.recipient?.toLowerCase().includes(s) || r.subject?.toLowerCase().includes(s))) return false;
    }
    return true;
  });

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search recipient or subject"
            className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="history-search" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="history-status-filter">
          <option value="">All statuses</option>
          <option value="sent">Sent</option>
          <option value="delivered">Delivered</option>
          <option value="opened">Opened</option>
          <option value="skipped">Skipped</option>
          <option value="failed">Failed</option>
          <option value="bounced">Bounced</option>
          <option value="suppressed">Suppressed</option>
        </select>
      </div>

      {loading ? (
        <Loader />
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-left px-4 py-2.5">Subject · Recipient</th>
                <th className="text-left px-4 py-2.5">Template</th>
                <th className="text-left px-4 py-2.5">Sent by</th>
                <th className="text-left px-4 py-2.5">When</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`history-row-${r.id}`}>
                  <td className="px-4 py-2.5"><StatusPill status={r.status} /></td>
                  <td className="px-4 py-2.5">
                    <div className="font-medium text-slate-900 truncate max-w-md">{r.subject || "(no subject)"}</div>
                    <div className="text-xs text-slate-500">→ {r.recipient}</div>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-600"><code>{r.template_key || "—"}</code></td>
                  <td className="px-4 py-2.5 text-xs text-slate-500">{r.initiated_by === "system" ? "automation" : r.initiated_by}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-500 whitespace-nowrap">{formatWhen(r.sent_at || r.created_at)}</td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500 text-sm">No emails match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// -------------------- Templates --------------------
function TemplatesPane() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get("/comms/templates")
      .then(r => setRows(r.data || []))
      .finally(() => setLoading(false));
  }, []);

  const filtered = categoryFilter ? rows.filter(r => r.category === categoryFilter) : rows;

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
          className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
          <option value="">All categories</option>
          <option value="transactional">Transactional</option>
          <option value="internal">Internal</option>
          <option value="marketing">Marketing</option>
          <option value="nurture">Nurture</option>
          <option value="newsletter">Newsletter</option>
          <option value="product_updates">Product Updates</option>
        </select>
        <div className="text-sm text-slate-500">{filtered.length} templates</div>
      </div>

      {loading ? <Loader /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map(t => (
            <div key={t.key} className="border border-slate-200 rounded-lg p-4 bg-white hover:shadow-sm transition"
              data-testid={`template-card-${t.key}`}>
              <div className="mb-2"><DeliveryBadge mode={t.delivery_mode} testid={`template-delivery-${t.key}`} /></div>
              <div className="flex items-start justify-between mb-2">
                <div className="min-w-0">
                  <div className="font-medium text-slate-900 text-sm truncate">{t.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5"><code>{t.key}</code></div>
                </div>
                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full whitespace-nowrap ${
                  t.category === "transactional" ? "bg-blue-100 text-blue-700" :
                  t.category === "internal" ? "bg-slate-200 text-slate-700" :
                  "bg-purple-100 text-purple-700"
                }`}>{t.category}</span>
              </div>
              <div className="text-xs text-slate-600 mb-3 line-clamp-2">{t.subject}</div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPreview(t)}
                  className="inline-flex items-center gap-1 text-xs text-[#0066FF] hover:text-[#0055D4]"
                  data-testid={`template-preview-${t.key}`}
                >
                  <Eye className="w-3.5 h-3.5" /> Preview
                </button>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${t.enabled ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                  {t.enabled ? "Enabled" : "Disabled"}
                </span>
                <span className="flex-1" />
                <SendSampleButton templateKey={t.key} />
              </div>
            </div>
          ))}
        </div>
      )}

      {preview && <TemplatePreviewModal templateKey={preview.key} onClose={() => setPreview(null)} />}
    </div>
  );
}

function TemplatePreviewModal({ templateKey, onClose }) {
  const [data, setData] = useState(null);
  const [mode, setMode] = useState("sample"); // sample | live
  const [loanOptions, setLoanOptions] = useState([]);
  const [selectedLoan, setSelectedLoan] = useState("");
  const [showEdit, setShowEdit] = useState(false);
  const [editSubject, setEditSubject] = useState("");
  const [editBody, setEditBody] = useState("");
  const [showTest, setShowTest] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tplMode, setTplMode] = useState("test");

  const loadPreview = React.useCallback(() => {
    setData(null);
    const url = mode === "live" && selectedLoan
      ? `/comms/templates/${templateKey}/preview-live?loan_id=${selectedLoan}`
      : `/comms/templates/${templateKey}/preview`;
    api.get(url).then(r => {
      setData(r.data);
      setEditSubject(r.data?.subject || "");
    });
  }, [templateKey, mode, selectedLoan]);

  useEffect(() => { loadPreview(); }, [loadPreview]);
  useEffect(() => {
    api.get("/operations/loans?limit=100").then(r => setLoanOptions(r.data || []));
    api.get(`/comms/templates/${templateKey}`).then(r => { setEditBody(r.data?.body_html || ""); setTplMode(r.data?.delivery_mode || "test"); });
    api.get("/comms/delivery-modes").then(r => setTestEmail((v) => v || r.data?.test_recipient || ""));
  }, [templateKey]);

  const doSendTest = async () => {
    if (!testEmail || !testEmail.includes("@")) return;
    setSending(true);
    try {
      const payload = { recipient: testEmail };
      if (mode === "live" && selectedLoan) payload.loan_id = selectedLoan;
      const { data: res } = await api.post(`/comms/templates/${templateKey}/send-test`, payload);
      if (res.status === "sent") toast.success(`Sample sent to ${testEmail}`);
      else if (res.status === "skipped" || res.status === "blocked") toast.warning(`Not sent: ${res.reason}`);
      else toast.error(`Failed: ${res.error || "unknown"}`);
      setShowTest(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); } finally { setSending(false); }
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.patch(`/comms/templates/${templateKey}`, {
        subject: editSubject,
        body_html: editBody,
      });
      toast.success("Template updated");
      setShowEdit(false);
      loadPreview();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()} data-testid="template-preview-modal">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">
              {mode === "live" ? "Live loan data" : "Sample data"} · {templateKey}
            </div>
            <h3 className="font-semibold text-slate-900 truncate">{data?.subject || "…"}</h3>
          </div>
          <DeliveryBadge mode={tplMode} size="lg" testid="template-modal-delivery" />
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900"><X className="w-5 h-5" /></button>
        </div>

        {/* Controls */}
        <div className="px-6 py-3 border-b border-slate-100 bg-slate-50 flex items-center gap-2 flex-wrap">
          <div className="flex bg-white rounded-lg border border-slate-200 overflow-hidden text-xs">
            <button onClick={() => setMode("sample")}
              className={`px-3 py-1.5 ${mode === "sample" ? "bg-slate-100 font-medium" : "hover:bg-slate-50"}`}
              data-testid="mode-sample">Sample data</button>
            <button onClick={() => setMode("live")}
              className={`px-3 py-1.5 ${mode === "live" ? "bg-slate-100 font-medium" : "hover:bg-slate-50"}`}
              data-testid="mode-live">Live loan data</button>
          </div>
          {mode === "live" && (
            <select value={selectedLoan} onChange={e => setSelectedLoan(e.target.value)}
              className="text-xs border border-slate-200 rounded-lg px-2 py-1.5" data-testid="live-loan-select">
              <option value="">Select a loan…</option>
              {loanOptions.slice(0, 60).map(l => <option key={l.loan_id} value={l.loan_id}>{l.loan_id} · {l.client_name}</option>)}
            </select>
          )}
          <div className="flex-1" />
          <button onClick={() => setShowEdit(v => !v)}
            className="text-xs px-3 py-1.5 border border-slate-200 bg-white rounded-lg hover:bg-slate-100"
            data-testid="template-edit-toggle">
            {showEdit ? "Close editor" : "Edit template"}
          </button>
          <button onClick={() => setShowTest(true)}
            className="text-xs px-3 py-1.5 bg-[#0066FF] text-white rounded-lg hover:bg-[#0055D4]"
            data-testid="send-test-open">
            Send test
          </button>
        </div>

        <div className="flex-1 overflow-hidden bg-slate-100 flex">
          {showEdit ? (
            <div className="w-full h-full flex flex-col p-4 bg-white gap-3 overflow-y-auto">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-500">Subject (Jinja2 supported)</label>
                <input value={editSubject} onChange={e => setEditSubject(e.target.value)}
                  className="w-full mt-1 border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono" data-testid="edit-subject" />
              </div>
              <div className="flex-1">
                <label className="text-[11px] uppercase tracking-wider text-slate-500">Body HTML (Jinja2 supported)</label>
                <textarea value={editBody} onChange={e => setEditBody(e.target.value)}
                  className="w-full mt-1 border border-slate-300 rounded-lg px-3 py-2 text-xs font-mono h-64" data-testid="edit-body" />
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowEdit(false)} className="px-3 py-1.5 text-sm text-slate-600">Cancel</button>
                <button onClick={saveEdit} disabled={saving}
                  className="px-4 py-1.5 bg-[#0066FF] text-white text-sm rounded-lg disabled:opacity-50" data-testid="edit-save">
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          ) : data ? (
            <iframe title="Template preview" srcDoc={data.html} className="w-full h-full min-h-[500px] bg-white" />
          ) : (
            <div className="p-8"><Loader /></div>
          )}
        </div>

        {showTest && (
          <div className="absolute bottom-6 right-6 bg-white shadow-xl rounded-lg border border-slate-200 p-4 w-80" data-testid="send-test-panel">
            <div className="text-xs font-semibold text-slate-900 mb-2">Send sample email</div>
            <div className="text-[11px] text-slate-500 mb-2">Allowed: the test inbox or an admin's own email. The subject is marked [SAMPLE].</div>
            <input type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)}
              placeholder="your@email.com"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mb-2" data-testid="test-email-input" />
            <div className="text-[11px] text-slate-500 mb-2">
              {mode === "live" && selectedLoan ? `Uses live data from ${selectedLoan}` : "Uses sample data"}
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowTest(false)} className="text-xs text-slate-600">Cancel</button>
              <button onClick={doSendTest} disabled={sending}
                className="text-xs px-3 py-1.5 bg-[#0066FF] text-white rounded-lg" data-testid="send-test-fire">
                {sending ? "Sending…" : "Send"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// -------------------- Automations --------------------
function AutomationsPane() {
  const [rows, setRows] = useState([]);
  const [modes, setModes] = useState({});
  useEffect(() => {
    api.get("/comms/delivery-modes").then(r => setModes(Object.fromEntries((r.data?.templates || []).map(t => [t.key, t.delivery_mode]))));
  }, []);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    setLoading(true);
    api.get("/comms/automations").then(r => setRows(r.data || [])).finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const toggle = async (r) => {
    const nextEnabled = !r.enabled;
    // Optimistic UI — flip immediately so it feels responsive
    setRows((cur) => cur.map((x) => (x.id === r.id ? { ...x, enabled: nextEnabled } : x)));
    try {
      await api.patch(`/comms/automations/${r.id}`, { enabled: nextEnabled });
      toast.success(nextEnabled
        ? `Enabled: ${r.template_key}`
        : `Paused: ${r.template_key}`);
    } catch (e) {
      // Rollback on failure
      setRows((cur) => cur.map((x) => (x.id === r.id ? { ...x, enabled: r.enabled } : x)));
      toast.error(e?.response?.data?.detail || "Could not update automation.");
    }
  };

  if (loading) return <Loader />;

  const grouped = rows.reduce((acc, r) => {
    (acc[r.event_type] = acc[r.event_type] || []).push(r);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="automations-pane">
      {Object.entries(grouped).map(([event, autos]) => (
        <div key={event}>
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-2"><code>{event}</code></div>
          <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
            {autos.map((a, i) => (
              <div key={a.id} className={`flex items-center gap-4 px-4 py-3 ${i > 0 ? "border-t border-slate-100" : ""}`}
                data-testid={`automation-row-${a.id}`}>
                <button onClick={() => toggle(a)} data-testid={`automation-toggle-${a.id}`}>
                  {a.enabled
                    ? <ToggleRight className="w-8 h-8 text-[#0066FF]" />
                    : <ToggleLeft className="w-8 h-8 text-slate-400" />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 flex items-center gap-2">{a.template_key} <DeliveryBadge mode={modes[a.template_key] || "test"} testid={`automation-delivery-${a.id}`} /></div>
                  <div className="text-xs text-slate-500">
                    → {a.recipient_class}
                    {a.internal_recipient_list && <> · list: <code>{a.internal_recipient_list}</code></>}
                    {a.recipient_override && <> · to: {a.recipient_override}</>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// -------------------- Distribution Lists --------------------
function DistributionListsPane() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState("");
  const load = useCallback(() => {
    setLoading(true);
    api.get("/comms/distribution-lists").then(r => setRows(r.data || [])).finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const save = async () => {
    const emails = draft.split(/[,\n]/).map(e => e.trim()).filter(Boolean);
    await api.patch(`/comms/distribution-lists/${editing.key}`, { emails });
    setEditing(null);
    load();
  };

  if (loading) return <Loader />;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="distribution-lists-pane">
      {rows.map(l => (
        <div key={l.key} className="border border-slate-200 rounded-lg p-4 bg-white">
          <div className="flex items-start justify-between mb-2">
            <div>
              <div className="font-medium text-slate-900">{l.name}</div>
              <div className="text-xs text-slate-500"><code>{l.key}</code> · {l.description}</div>
            </div>
            <button onClick={() => { setEditing(l); setDraft((l.emails || []).join("\n")); }}
              className="text-xs text-[#0066FF] hover:text-[#0055D4]" data-testid={`edit-list-${l.key}`}>Edit</button>
          </div>
          <div className="space-y-1">
            {(l.emails || []).length === 0 ? (
              <div className="text-xs text-slate-400 italic">No recipients configured — internal notifications will fall back to INTERNAL_NOTIFY_EMAIL.</div>
            ) : l.emails.map(e => <div key={e} className="text-xs text-slate-700">{e}</div>)}
          </div>
        </div>
      ))}

      {editing && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-200">
              <h3 className="font-semibold text-slate-900">{editing.name}</h3>
              <p className="text-xs text-slate-500 mt-0.5">{editing.description}</p>
            </div>
            <div className="px-6 py-4">
              <label className="text-xs uppercase tracking-wider text-slate-500">Email addresses (one per line)</label>
              <textarea value={draft} onChange={e => setDraft(e.target.value)} rows={6}
                placeholder="user@instabridgefunding.com"
                className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono"
                data-testid="list-emails-textarea" />
            </div>
            <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm text-slate-600">Cancel</button>
              <button onClick={save} className="px-4 py-2 bg-[#0066FF] text-white text-sm font-medium rounded-lg"
                data-testid="list-save-btn">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// -------------------- Suppressions --------------------
function SuppressionsPane() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const load = useCallback(() => {
    setLoading(true);
    api.get("/comms/suppressions").then(r => setRows(r.data || [])).finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const add = async () => {
    if (!newEmail || !newEmail.includes("@")) return;
    await api.post("/comms/suppressions", { email: newEmail, reason: "admin" });
    setNewEmail(""); setAdding(false); load();
  };
  const remove = async (email) => {
    if (!confirm(`Remove ${email} from suppression list?`)) return;
    await api.delete(`/comms/suppressions/${encodeURIComponent(email)}`);
    load();
  };

  if (loading) return <Loader />;

  return (
    <div data-testid="suppressions-pane">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500">Addresses that will never receive any email (auto-added from bounces/complaints).</p>
        <button onClick={() => setAdding(true)}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-[#0066FF] text-white rounded-lg hover:bg-[#0055D4]"
          data-testid="suppression-add-btn">
          <Plus className="w-4 h-4" /> Add
        </button>
      </div>
      {adding && (
        <div className="mb-4 flex gap-2">
          <input value={newEmail} onChange={e => setNewEmail(e.target.value)}
            placeholder="email@example.com"
            className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm max-w-sm" />
          <button onClick={add} className="px-3 py-2 bg-slate-900 text-white text-sm rounded-lg" data-testid="suppression-save-btn">Add</button>
          <button onClick={() => setAdding(false)} className="px-3 py-2 text-sm text-slate-500">Cancel</button>
        </div>
      )}
      <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
        {rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">No suppressions.</div>
        ) : rows.map(r => (
          <div key={r.id} className="flex items-center justify-between px-4 py-3 border-b border-slate-100 last:border-0">
            <div>
              <div className="text-sm font-medium text-slate-900">{r.email}</div>
              <div className="text-xs text-slate-500">{r.reason} · {formatWhen(r.created_at)}</div>
            </div>
            <button onClick={() => remove(r.email)} className="text-slate-400 hover:text-red-600" data-testid={`suppression-remove-${r.email}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// -------------------- Small helpers --------------------
function Loader() {
  return <div className="flex items-center gap-2 text-sm text-slate-500 py-8">
    <Loader2 className="w-4 h-4 animate-spin" /> Loading…
  </div>;
}

function StatusPill({ status }) {
  const map = {
    sent:      "bg-blue-100 text-blue-700",
    delivered: "bg-green-100 text-green-700",
    opened:    "bg-emerald-100 text-emerald-700",
    clicked:   "bg-emerald-100 text-emerald-700",
    skipped:   "bg-amber-100 text-amber-800",
    suppressed:"bg-amber-100 text-amber-800",
    failed:    "bg-red-100 text-red-700",
    bounced:   "bg-red-100 text-red-700",
    complained:"bg-red-100 text-red-700",
    queued:    "bg-slate-100 text-slate-700",
  };
  return <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${map[status] || map.queued}`}>{status}</span>;
}

function formatWhen(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

// -------------------- Verifications --------------------
function VerificationsPane() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(null); // key: `${app_id}:${decision}`

  const load = useCallback(() => {
    setLoading(true);
    api.get("/comms/verifications")
      .then((r) => setRows(r.data || []))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const resolve = async (row, decision) => {
    const key = `${row.id}:${decision}`;
    const onFile = (row.attorney_verification || {}).on_file || null;
    if (decision === "use_plaintiff" && !window.confirm(
      `Send underwriting email to "${row.attorney_email}"? Stored attorney records will NOT be updated.`
    )) return;
    if (decision === "update_stored" && !window.confirm(
      `Update the stored email for ${row.attorney_name || "this attorney"} from "${onFile}" to "${row.attorney_email}"?`
    )) return;

    setPending(key);
    try {
      await api.post("/comms/verifications/resolve", {
        application_id: row.id,
        decision,
        plaintiff_email: row.attorney_email,
        on_file_email: onFile,
      });
      toast.success("Verification resolved.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resolve.");
    } finally {
      setPending(null);
    }
  };

  return (
    <div data-testid="verifications-pane">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Pending attorney verifications</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Applications where the plaintiff-entered attorney email couldn&apos;t be auto-matched. Approve one to release the held underwriting email.
          </p>
        </div>
        <button
          onClick={load}
          className="text-xs text-[#0066FF] hover:underline"
          data-testid="verifications-refresh"
        >
          Refresh
        </button>
      </div>

      {loading ? (
        <Loader />
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-slate-200 rounded-lg p-10 text-center">
          <ShieldAlert className="w-6 h-6 text-slate-300 mx-auto mb-2" />
          <div className="text-sm text-slate-600 font-medium">No pending verifications</div>
          <div className="text-xs text-slate-400 mt-1">All attorney emails are cleared. 🎉</div>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => {
            const v = row.attorney_verification || {};
            const onFile = row.on_file_user;
            return (
              <div
                key={row.id}
                className="border border-slate-200 rounded-xl p-5 bg-white"
                data-testid={`verification-row-${row.id}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <a
                        href={`/admin/applications/${row.id}`}
                        className="text-sm font-semibold text-[#0066FF] hover:underline"
                      >
                        {row.name}
                      </a>
                      <span className="text-xs text-slate-400">·</span>
                      <span className="text-xs text-slate-500 font-mono">{row.id?.slice(0, 8).toUpperCase()}</span>
                      <span className="inline-flex items-center px-2 h-5 text-[10px] uppercase tracking-wider rounded border bg-amber-50 text-amber-800 border-amber-200">
                        Held
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Submitted {formatWhen(row.created_at)} · {(v.reason || "").replaceAll("_", " ")}
                    </div>
                  </div>
                  <a
                    href={`/admin/applications/${row.id}`}
                    className="text-xs text-slate-500 hover:text-[#0066FF] hover:underline"
                  >
                    Open application →
                  </a>
                </div>

                <div className="grid sm:grid-cols-2 gap-3 mt-4">
                  <div className="border border-amber-200 bg-amber-50 rounded-lg p-3">
                    <div className="uppercase-label mb-1">Entered by plaintiff</div>
                    <div className="text-sm font-medium text-slate-900">{row.attorney_name || "—"}</div>
                    <div className="text-sm text-slate-700 break-all">{row.attorney_email}</div>
                    {row.attorney_firm && <div className="text-xs text-slate-500 mt-1">{row.attorney_firm}</div>}
                  </div>
                  {onFile ? (
                    <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-3">
                      <div className="uppercase-label mb-1">On file</div>
                      <div className="text-sm font-medium text-slate-900">{onFile.name || "—"}</div>
                      <div className="text-sm text-slate-700 break-all">{onFile.email}</div>
                      {onFile.role && <div className="text-xs text-slate-500 mt-1">{onFile.role}</div>}
                    </div>
                  ) : (
                    <div className="border border-dashed border-slate-200 rounded-lg p-3 text-xs text-slate-500 italic">
                      No matching attorney on file.
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-2 mt-4">
                  {v.on_file && (
                    <button
                      onClick={() => resolve(row, "use_verified")}
                      disabled={pending?.startsWith(`${row.id}:`)}
                      className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-60"
                      data-testid={`verification-use-verified-${row.id}`}
                    >
                      {pending === `${row.id}:use_verified` && <Loader2 className="w-3 h-3 animate-spin" />}
                      Use verified email
                    </button>
                  )}
                  <button
                    onClick={() => resolve(row, "use_plaintiff")}
                    disabled={pending?.startsWith(`${row.id}:`)}
                    className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-amber-300 text-amber-800 hover:bg-amber-50 disabled:opacity-60"
                    data-testid={`verification-use-plaintiff-${row.id}`}
                  >
                    {pending === `${row.id}:use_plaintiff` && <Loader2 className="w-3 h-3 animate-spin" />}
                    Use plaintiff email
                  </button>
                  {v.on_file && (
                    <button
                      onClick={() => resolve(row, "update_stored")}
                      disabled={pending?.startsWith(`${row.id}:`)}
                      className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                      data-testid={`verification-update-stored-${row.id}`}
                    >
                      {pending === `${row.id}:update_stored` && <Loader2 className="w-3 h-3 animate-spin" />}
                      Update stored email
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
